import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useStudio } from '@/store/StudioStore';
import { compileMasterPrompt, buildVariations, buildRecipe, buildMockupPrompt, buildCharacterSheetPrompt } from '@/lib/promptCompiler';
import { storage, genId } from '@/lib/storage';

export default function Result() {
  const { state, dispatch } = useStudio();
  const nav = useNavigate();
  const [edited, setEdited] = useState(null);
  const [showMockup, setShowMockup] = useState(false);
  const [mockupProduct, setMockupProduct] = useState('a plain black t-shirt');

  const master = useMemo(() => compileMasterPrompt(state), [state]);
  const variations = useMemo(() => buildVariations(state), [state]);
  const recipe = useMemo(() => buildRecipe(state), [state]);
  const charSheet = useMemo(() => buildCharacterSheetPrompt(state), [state]);

  const finalPrompt = edited ?? master;

  React.useEffect(() => {
    storage.addHistory({ id: genId(), at: new Date().toISOString(), name: state.exactText || 'Untitled', prompt: master });
  }, []);

  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };
  const copyAllVariations = () => {
    const bundle = variations.map((v, i) => `--- VARIATION ${i + 1} · ${v.name} ---\n${v.prompt}`).join('\n\n');
    copy(bundle, 'All variations');
  };
  const download = () => storage.downloadText(finalPrompt, `type-elite-${(state.exactText || 'prompt').replace(/\s+/g, '-')}.txt`);
  const save = () => {
    const project = { ...state, id: state.id || genId(), savedAt: new Date().toISOString() };
    storage.saveProject(project);
    toast.success('Saved to My Projects');
  };
  const exportJSON = () => storage.exportProject({ ...state, name: state.exactText || 'Project' });
  const restore = () => setEdited(null);
  const buildMock = () => copy(buildMockupPrompt(finalPrompt, mockupProduct), 'Mockup prompt');

  return (
    <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10" data-testid="result-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow">Prompt Results</div>
          <h1 className="font-display text-4xl">&ldquo;{state.exactText || 'Untitled'}&rdquo;</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="back-to-studio" onClick={() => nav('/studio')} className="chrome-border chrome-hover rounded-full px-5 py-2 text-xs uppercase tracking-widest">Edit Recipe</button>
          <button data-testid="save-project" onClick={save} className="chrome-border chrome-hover rounded-full px-5 py-2 text-xs uppercase tracking-widest">Save Project</button>
          <button data-testid="export-json" onClick={exportJSON} className="chrome-border chrome-hover rounded-full px-5 py-2 text-xs uppercase tracking-widest">Export JSON</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-4">
          <div className="rounded-2xl chrome-border bg-zinc-900/70 p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="eyebrow">Master Prompt</div>
                <h2 className="font-display text-2xl">Copy this into your AI image tool</h2>
              </div>
              <div className="flex gap-2">
                <button data-testid="copy-master" onClick={() => copy(finalPrompt, 'Master prompt')} className="btn-magenta rounded-full px-4 py-2 text-xs uppercase tracking-widest">Copy</button>
                <button data-testid="download-master" onClick={download} className="chrome-border chrome-hover rounded-full px-4 py-2 text-xs uppercase tracking-widest">Download TXT</button>
                {edited != null && <button data-testid="restore-master" onClick={restore} className="chrome-border chrome-hover rounded-full px-4 py-2 text-xs uppercase tracking-widest">Restore</button>}
              </div>
            </div>
            <textarea data-testid="master-prompt-area" value={finalPrompt} onChange={(e) => setEdited(e.target.value)} rows={16} className="font-mono w-full bg-zinc-950 border border-zinc-800 rounded-lg p-4 text-sm text-zinc-100 focus:border-pink-500 outline-none" />
          </div>

          <div className="rounded-2xl chrome-border bg-zinc-900/70 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="eyebrow">5 Prompt Variations</div>
              <button data-testid="copy-all-variations" onClick={copyAllVariations} className="chrome-border chrome-hover rounded-full px-4 py-2 text-xs uppercase tracking-widest">Copy All</button>
            </div>
            <div className="space-y-3">
              {variations.map((v, i) => (
                <details key={i} className="chrome-border rounded-lg p-3 bg-zinc-950" data-testid={`variation-${i}`}>
                  <summary className="cursor-pointer flex items-center justify-between">
                    <span className="text-sm">{i + 1}. {v.name}</span>
                    <button onClick={(e) => { e.preventDefault(); copy(v.prompt, `Variation ${i + 1}`); }} className="text-xs uppercase tracking-widest text-pink-500">Copy</button>
                  </summary>
                  <pre className="font-mono text-xs whitespace-pre-wrap break-safe text-zinc-300 mt-2">{v.prompt}</pre>
                </details>
              ))}
            </div>
          </div>

          <div className="rounded-2xl chrome-border bg-zinc-900/70 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="eyebrow">Optional · Product Mockup Prompt</div>
              <button data-testid="toggle-mockup" onClick={() => setShowMockup((s) => !s)} className="chrome-border chrome-hover rounded-full px-4 py-2 text-xs uppercase tracking-widest">{showMockup ? 'Hide' : 'Show'}</button>
            </div>
            {showMockup && (
              <div className="space-y-3">
                <input data-testid="mockup-product" value={mockupProduct} onChange={(e) => setMockupProduct(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" placeholder="Which product?" />
                <button data-testid="build-mockup" onClick={buildMock} className="btn-magenta rounded-full px-4 py-2 text-xs uppercase tracking-widest">Build & Copy Mockup Prompt</button>
                <p className="text-xs text-zinc-500">This is a separate output. Your transparent master prompt above stays untouched.</p>
              </div>
            )}
          </div>

          {charSheet && (
            <div className="rounded-2xl chrome-border bg-zinc-900/70 p-5" data-testid="charsheet-block">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="eyebrow">Alphabet / Character Sheet Prompt</div>
                  <p className="text-xs text-zinc-400">Creates a lettering-artwork sheet, not an installable font.</p>
                </div>
                <button data-testid="copy-charsheet" onClick={() => copy(charSheet, 'Character sheet prompt')} className="btn-magenta rounded-full px-4 py-2 text-xs uppercase tracking-widest">Copy</button>
              </div>
              <pre className="font-mono text-xs whitespace-pre-wrap break-safe text-zinc-300 max-h-[280px] overflow-y-auto">{charSheet}</pre>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="rounded-2xl chrome-border bg-zinc-900/70 p-5 sticky top-20">
            <div className="eyebrow mb-2">Design Recipe</div>
            <div className="space-y-2 max-h-[600px] overflow-y-auto scrollbar-thin pr-2">
              {recipe.map((e, i) => (
                <div key={i} className="flex justify-between gap-3 text-xs border-b border-white/5 pb-1">
                  <span className="text-zinc-500 uppercase tracking-wider">{e.k}</span>
                  <span className="text-zinc-100 text-right">{e.v}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-3">Reference uploads must be attached in your outside AI tool. Local browser save may be cleared if you clear site data — export important recipes as JSON.</p>
          </div>
        </aside>
      </div>
    </main>
  );
}
